import { useTranslations } from '@vocab/react';
import {
  Text,
  Stack,
  RadioGroup,
  RadioItem,
  Strong,
  Loader,
  Badge,
  Heading,
} from 'braid-design-system';
import React, { type FormEvent } from 'react';

import { useAnalytics } from 'src/modules/analytics/AnalyticsProvider';
import type { AnalyticsProfileVisibilityLevel } from 'src/modules/analytics/types';
import { useAppConfig } from 'src/modules/appConfig';
import * as Schema from 'src/types/graphql';

import { useGetProfileVisibility, useUpdateProfileVisibility } from '../data';

import translations from './.vocab';

export const EditProfileVisibilitySettings = () => {
  const { t } = useTranslations(translations);
  const { brand, isUnifiedSite } = useAppConfig();
  const { profileVisibilityLevel, loading, error } = useGetProfileVisibility();
  const { profileVisibilityUpdate: profileVisibilityUpdateAnalytics } =
    useAnalytics();

  const [updateProfileVisibility] = useUpdateProfileVisibility();

  const DEFAULT_PROFILE_VISIBILITY_OPTION_DESCRIPTION = {
    Standard: t('Employers can access my Profile and resumé and...', {
      brand,
    }),
    Limited: t('Employers can access my Profile, but not my resumé...', {
      brand,
    }),
    Hidden: t('Employers cannot search for me...'),
  };

  const JOBSDB_LEGACY_PROFILE_VISIBILITY_OPTION_DESCRIPTION = {
    Standard: t('Highest chance for employers to contact you'),
    Limited: t('Employers can invite you to apply for jobs'),
    Hidden: t(
      'Relevant employers cannot contact you with exciting opportunities',
    ),
  };

  const matchProfileVisibilityOptionDescription = (
    level: Schema.VisibilityLevel,
  ) =>
    brand === 'Jobsdb' && !isUnifiedSite
      ? JOBSDB_LEGACY_PROFILE_VISIBILITY_OPTION_DESCRIPTION[level]
      : DEFAULT_PROFILE_VISIBILITY_OPTION_DESCRIPTION[level];

  const onChangeFn = ({
    currentTarget: { value },
  }: FormEvent<HTMLInputElement>) => {
    updateProfileVisibility({
      profileVisibility: {
        level: value as Schema.VisibilityLevel,
      },
    });
    profileVisibilityUpdateAnalytics(
      value.toLowerCase() as AnalyticsProfileVisibilityLevel,
    );
  };

  return loading || error ? (
    <Loader size="small" />
  ) : (
    <Stack space="large">
      <Heading level="4">{t('Searchable by employers')}</Heading>
      <Text tone="secondary">
        {t(
          `Your profile visibility setting controls...${
            brand === 'Jobsdb' && !isUnifiedSite ? '(Jobsdb legacy)' : ''
          }`,
          {
            LineBreak: () => <br />,
          },
        )}
      </Text>
      {
        <RadioGroup
          id="option"
          aria-label={t('Search settings')}
          onChange={onChangeFn}
          value={profileVisibilityLevel || ''}
          data={{ automation: 'profile-visibility-options' }}
        >
          <RadioItem
            data={{ automation: 'standard-radio-option' }}
            badge={<Badge tone="promote">{t('Recommended')}</Badge>}
            label={<Strong>{t('Standard')}</Strong>}
            description={matchProfileVisibilityOptionDescription(
              Schema.VisibilityLevel.Standard,
            )}
            value="Standard"
          />
          <RadioItem
            data={{ automation: 'limited-radio-option' }}
            label={<Strong>{t('Limited')}</Strong>}
            description={matchProfileVisibilityOptionDescription(
              Schema.VisibilityLevel.Limited,
            )}
            value="Limited"
          />
          <RadioItem
            data={{ automation: 'hidden-radio-option' }}
            label={<Strong>{t('Hidden')}</Strong>}
            description={matchProfileVisibilityOptionDescription(
              Schema.VisibilityLevel.Hidden,
            )}
            value="Hidden"
          />
        </RadioGroup>
      }
    </Stack>
  );
};
